import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Lock, Loader2, LogOut, Trash2, PlusCircle, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import {
  adminLogin,
  adminMe,
  getCharitySummary,
  getCharityUpdates,
  saveCharitySummary,
  createCharityUpdate,
  deleteCharityUpdate,
} from "@/api";

const TOKEN_KEY = "ezrueda_admin_token";
const EASE = [0.16, 1, 0.3, 1];

const inputCls =
  "w-full rounded-xl bg-white/5 border border-white/10 px-5 py-3.5 text-sm text-white placeholder:text-zinc-500 outline-none focus:border-brand-yellow/60 focus:ring-2 focus:ring-brand-yellow/20 transition-all duration-300";

const errText = (e) => {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x?.msg || "").filter(Boolean).join(" ");
  return "Something went wrong. Please try again.";
};

function LoginForm({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await adminLogin(email, password);
      localStorage.setItem(TOKEN_KEY, data.token);
      onLogin(data);
    } catch (err) {
      setError(errText(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.7, ease: EASE }}
      className="w-full max-w-md rounded-3xl border border-white/10 bg-brand-surface p-8 md:p-10"
      data-testid="admin-login-panel"
    >
      <div className="flex items-center gap-3 mb-8">
        <span className="flex items-center justify-center w-11 h-11 rounded-2xl bg-brand-yellow/10 border border-brand-yellow/30">
          <Lock className="w-5 h-5 text-brand-yellow" />
        </span>
        <div>
          <h1 className="font-display font-semibold text-2xl tracking-tight">Admin sign in</h1>
          <p className="text-xs text-zinc-500">EzRueda Tips Fund management</p>
        </div>
      </div>
      <form onSubmit={submit} className="space-y-4" data-testid="admin-login-form">
        <input
          required
          type="email"
          placeholder="Admin email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className={inputCls}
          data-testid="admin-email-input"
        />
        <input
          required
          type="password"
          placeholder="Password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          className={inputCls}
          data-testid="admin-password-input"
        />
        {error && (
          <p className="text-sm text-red-400" data-testid="admin-login-error">
            {error}
          </p>
        )}
        <button
          type="submit"
          disabled={loading}
          data-testid="admin-login-button"
          className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-brand-yellow text-black font-bold px-8 py-4 hover:bg-brand-yellow-hover hover:scale-[1.02] disabled:opacity-60 transition-all duration-300"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Sign in"}
        </button>
      </form>
    </motion.div>
  );
}

function TotalsCard({ token, summary, onSaved }) {
  const [form, setForm] = useState({
    total_donated: summary?.total_donated ?? 0,
    tips_count: summary?.tips_count ?? 0,
    drivers_supported: summary?.drivers_supported ?? 0,
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (summary) {
      setForm({
        total_donated: summary.total_donated ?? 0,
        tips_count: summary.tips_count ?? 0,
        drivers_supported: summary.drivers_supported ?? 0,
      });
    }
  }, [summary]);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await saveCharitySummary(token, {
        total_donated: parseFloat(form.total_donated) || 0,
        tips_count: parseInt(form.tips_count, 10) || 0,
        drivers_supported: parseInt(form.drivers_supported, 10) || 0,
      });
      onSaved(data);
      toast.success("Totals updated", { description: "The public page now shows the new numbers." });
    } catch (err) {
      toast.error(errText(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <form
      onSubmit={save}
      className="rounded-3xl border border-white/10 bg-brand-surface p-8"
      data-testid="admin-totals-form"
    >
      <h2 className="font-display font-semibold text-2xl tracking-tight mb-2">Fund totals</h2>
      <p className="text-xs text-zinc-500 mb-7">
        These feed the public "Where your tips go" page and the rider app.
      </p>
      <div className="grid sm:grid-cols-3 gap-4 mb-6">
        <label className="block">
          <span className="block text-[10px] font-bold tracking-[0.2em] uppercase text-zinc-500 mb-2">
            Total donated ($)
          </span>
          <input
            required
            type="number"
            step="0.01"
            min="0"
            value={form.total_donated}
            onChange={(e) => setForm({ ...form, total_donated: e.target.value })}
            className={inputCls}
            data-testid="admin-total-input"
          />
        </label>
        <label className="block">
          <span className="block text-[10px] font-bold tracking-[0.2em] uppercase text-zinc-500 mb-2">
            Tips count
          </span>
          <input
            type="number"
            min="0"
            value={form.tips_count}
            onChange={(e) => setForm({ ...form, tips_count: e.target.value })}
            className={inputCls}
            data-testid="admin-tips-count-input"
          />
        </label>
        <label className="block">
          <span className="block text-[10px] font-bold tracking-[0.2em] uppercase text-zinc-500 mb-2">
            Drivers supported
          </span>
          <input
            type="number"
            min="0"
            value={form.drivers_supported}
            onChange={(e) => setForm({ ...form, drivers_supported: e.target.value })}
            className={inputCls}
            data-testid="admin-drivers-input"
          />
        </label>
      </div>
      <button
        type="submit"
        disabled={saving}
        data-testid="admin-save-totals-button"
        className="inline-flex items-center gap-2 rounded-full bg-brand-yellow text-black font-bold px-7 py-3 text-sm hover:bg-brand-yellow-hover hover:scale-[1.02] disabled:opacity-60 transition-all duration-300"
      >
        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save totals"}
      </button>
    </form>
  );
}

function UpdateForm({ token, onCreated }) {
  const empty = { title: "", tag: "", date: new Date().toISOString().slice(0, 10), amount: "", body: "" };
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        title: form.title,
        tag: form.tag,
        date: form.date,
        body: form.body,
        ...(form.amount !== "" ? { amount: parseFloat(form.amount) } : {}),
      };
      const { data } = await createCharityUpdate(token, payload);
      onCreated(data);
      setForm(empty);
      toast.success("Story published", { description: "It's live on the public tips page." });
    } catch (err) {
      toast.error(errText(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <form
      onSubmit={submit}
      className="rounded-3xl border border-white/10 bg-brand-surface p-8"
      data-testid="admin-update-form"
    >
      <h2 className="font-display font-semibold text-2xl tracking-tight mb-7 flex items-center gap-3">
        <PlusCircle className="w-5 h-5 text-brand-green" /> Post a new update
      </h2>
      <div className="grid sm:grid-cols-2 gap-4 mb-4">
        <input
          required
          type="text"
          placeholder="Story title"
          value={form.title}
          onChange={(e) => setForm({ ...form, title: e.target.value })}
          className={inputCls}
          data-testid="update-title-input"
        />
        <input
          required
          type="text"
          placeholder="Tag (e.g. Community, Driver Relief)"
          value={form.tag}
          onChange={(e) => setForm({ ...form, tag: e.target.value })}
          className={inputCls}
          data-testid="update-tag-input"
        />
        <input
          required
          type="date"
          value={form.date}
          onChange={(e) => setForm({ ...form, date: e.target.value })}
          className={`${inputCls} [color-scheme:dark]`}
          data-testid="update-date-input"
        />
        <input
          type="number"
          step="0.01"
          min="0"
          placeholder="Amount ($, optional)"
          value={form.amount}
          onChange={(e) => setForm({ ...form, amount: e.target.value })}
          className={inputCls}
          data-testid="update-amount-input"
        />
      </div>
      <textarea
        required
        rows={4}
        placeholder="Tell the story — where did the money go, who did it help?"
        value={form.body}
        onChange={(e) => setForm({ ...form, body: e.target.value })}
        className={`${inputCls} mb-6 resize-none`}
        data-testid="update-body-input"
      />
      <button
        type="submit"
        disabled={saving}
        data-testid="update-submit-button"
        className="inline-flex items-center gap-2 rounded-full bg-brand-green text-black font-bold px-7 py-3 text-sm hover:bg-brand-green-hover hover:scale-[1.02] disabled:opacity-60 transition-all duration-300"
      >
        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Publish update"}
      </button>
    </form>
  );
}

export default function Admin() {
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);
  const [summary, setSummary] = useState(null);
  const [updates, setUpdates] = useState([]);

  useEffect(() => {
    window.scrollTo(0, 0);
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) {
      setChecking(false);
      return;
    }
    adminMe(token)
      .then(({ data }) => setUser(data))
      .catch(() => localStorage.removeItem(TOKEN_KEY))
      .finally(() => setChecking(false));
  }, []);

  useEffect(() => {
    if (!user) return;
    getCharitySummary().then(({ data }) => setSummary(data)).catch(() => {});
    getCharityUpdates().then(({ data }) => setUpdates(data)).catch(() => {});
  }, [user]);

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setUser(null);
  };

  if (checking) {
    return (
      <main className="min-h-screen flex items-center justify-center" data-testid="admin-loading">
        <Loader2 className="w-8 h-8 text-brand-yellow animate-spin" />
      </main>
    );
  }

  if (!user) {
    return (
      <main className="min-h-screen flex flex-col items-center justify-center px-6 pt-24 pb-16" data-testid="admin-page">
        <LoginForm onLogin={setUser} />
        <Link
          to="/"
          className="mt-8 inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-zinc-500 hover:text-white transition-colors duration-300"
          data-testid="admin-back-home"
        >
          <ArrowLeft className="w-3.5 h-3.5" /> Back to site
        </Link>
      </main>
    );
  }

  const token = localStorage.getItem(TOKEN_KEY);

  return (
    <main className="min-h-screen px-6 lg:px-10 pt-32 pb-24" data-testid="admin-panel">
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center justify-between mb-12">
          <div>
            <p className="text-xs font-bold tracking-[0.3em] uppercase text-zinc-500 mb-2">
              Tips Fund admin
            </p>
            <h1 className="font-display font-semibold text-4xl tracking-tight">
              Manage <span className="text-brand-yellow">impact.</span>
            </h1>
            <p className="text-xs text-zinc-500 mt-2" data-testid="admin-user-email">
              Signed in as {user.email}
            </p>
          </div>
          <button
            onClick={logout}
            data-testid="admin-logout-button"
            className="inline-flex items-center gap-2 rounded-full border border-white/15 text-zinc-300 text-sm font-semibold px-5 py-2.5 hover:border-red-400/60 hover:text-red-300 transition-all duration-300"
          >
            <LogOut className="w-4 h-4" /> Log out
          </button>
        </div>

        <div className="space-y-8">
          <TotalsCard token={token} summary={summary} onSaved={setSummary} />
          <UpdateForm token={token} onCreated={(u) => setUpdates([u, ...updates])} />

          <div className="rounded-3xl border border-white/10 bg-brand-surface p-8" data-testid="admin-updates-list">
            <h2 className="font-display font-semibold text-2xl tracking-tight mb-7">
              Published updates ({updates.length})
            </h2>
            {updates.length === 0 ? (
              <p className="text-sm text-zinc-500">No stories yet.</p>
            ) : (
              <ul className="divide-y divide-white/10">
                {updates.map((u) => (
                  <li key={u.id} className="py-5 flex items-start justify-between gap-6" data-testid={`admin-update-row-${u.id}`}>
                    <div>
                      <p className="text-[10px] font-bold tracking-[0.2em] uppercase text-brand-yellow mb-1.5">
                        {u.tag} · {u.date}
                      </p>
                      <p className="font-display font-medium text-lg tracking-tight">{u.title}</p>
                    </div>
                    <button
                      onClick={async () => {
                        try {
                          await deleteCharityUpdate(token, u.id);
                          setUpdates(updates.filter((x) => x.id !== u.id));
                          toast.success("Update deleted");
                        } catch (err) {
                          toast.error(errText(err));
                        }
                      }}
                      data-testid={`update-delete-${u.id}`}
                      className="shrink-0 inline-flex items-center gap-2 rounded-full border border-white/15 text-zinc-400 text-xs font-semibold px-4 py-2 hover:border-red-400/60 hover:text-red-300 transition-all duration-300"
                      aria-label={`Delete ${u.title}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" /> Delete
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
    </main>
  );
}
